// FeatureList.js
import React, { useEffect, useState, useCallback } from 'react';
import { Icon, Page, Card, ResourceList, TextStyle } from '@shopify/polaris';
import { getSpently, postSpently } from '../util/spentlyRest';
import FeatureConfirmModal from '../widgets/builder/modals/FeatureConfirmModal';
import SwitchButton from '../components/SwitchButton';
import { Icons } from '../util/superGrossIconFile';

const FeatureList = () => {
  const [features, setFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const updateSelectedFeature = (previusFeature) => {
    const feature = { ...previusFeature };
    // toggle the feature's enabled status
    feature.enabled = !previusFeature.enabled;
    setSelectedFeature(feature);
  };

  useEffect(() => {
    // Fetch the list of features from an API
    const loadFeaturelist = async () => {
      const featureUrl = '/api/features';
      const featuresResponse = await getSpently(featureUrl);

      setFeatures(featuresResponse);
    };
    loadFeaturelist();
  }, []);

  // handle when user confirm the modal dialog
  const handleFeatureConfirm = useCallback(async () => {
    if (!selectedFeature) {
      return;
    }

    const featureBeforeUpdate = features.find(
      (f) => f.key === selectedFeature.key
    );
    updateSelectedFeature(featureBeforeUpdate);
    setOpenModal(false);
  }, [features, selectedFeature]);

  useEffect(() => {
    // will not trigger sever call if the selected feature is null or the modal is open
    if (!selectedFeature || openModal) {
      return;
    }

    const updateFeatureFlag = async () => {
      const results = await getSpently(`/api/features/enabled`);
      sessionStorage.setItem(
        `spently-featureFlags`,
        results ? JSON.stringify(results.enabled) : '[]'
      );
    };

    const updateFeature = async () => {
      const result = await postSpently(`/api/feature`, {
        key: selectedFeature.key,
        enabled: selectedFeature.enabled,
      });

      if (result.reload) {
        // redirect user to re-authenticate for the new feature
        const shop = window.sessionStorage['spently-shop'];
        sessionStorage.clear();
        window.location.href = `${window.location.origin}?shop=${shop}`;
      } else {
        updateFeatureFlag();
      }
    };
    updateFeature();
  }, [openModal, selectedFeature]);

  // update the feature list when the selected feature changes
  useEffect(() => {
    // will not trigger sever call if the selected feature is null or the modal is open
    if (!selectedFeature || openModal) {
      return;
    }

    // If the selected feature changes, update the feature list
    setFeatures(() =>
      features.map((f) => {
        if (f.key === selectedFeature.key) {
          return { ...selectedFeature };
        }
        return f;
      })
    );
  }, [features, openModal, selectedFeature]);

  // handle when user click 'enable' button
  // we don't update the feature's enabled status directly
  // the feauture's enabled status will be only loaded from server
  const onToggle = async (key) => {
    const featureBeforeUpdate = features.find((f) => f.key === key);
    updateSelectedFeature(featureBeforeUpdate);

    // if the feature doesn't require any shopify scopes, we can enable it directly
    if (Object.keys(featureBeforeUpdate.shopifyScopes).length === 0) {
      updateSelectedFeature(featureBeforeUpdate);
    } else {
      // trigger to open modal
      setOpenModal(true);
    }
  };

  // handle cancel from the modal
  const handleCancel = useCallback(() => {
    setSelectedFeature(null);
    setOpenModal(false);
  }, []);

  // disbale the switch button if the feature require any shopify scopes and already enabled
  const disbaleSwitchButton = (feature) =>
    Object.keys(feature.shopifyScopes).length > 0 && feature.enabled;

  // render the feature card
  const featureCard = (cardTitle, cardFeatures) => (
    <Card title={cardTitle}>
      <div style={{ pointerEvents: 'none' }}>
        <ResourceList
          resourceName={{ singular: 'feature', plural: 'features' }}
          items={cardFeatures}
          renderItem={(item) => {
            const { key, featureName, featureDescription, enabled } = item;
            return (
              <ResourceList.Item id={key}>
                <div
                  style={{
                    display: 'flex',
                  }}
                >
                  <div
                    style={{
                      width: '50px',
                      paddingRight: '20px',
                      display: 'flex',
                    }}
                  >
                    <Icon source={() => Icons[key]} />
                  </div>

                  <div
                    style={{
                      flex: '1',
                    }}
                  >
                    <h3>
                      <TextStyle variation="strong">{featureName}</TextStyle>
                    </h3>
                    <p>{featureDescription}</p>
                  </div>
                  <SwitchButton
                    disabled={disbaleSwitchButton(item)}
                    checked={enabled}
                    onChange={() => onToggle(key)}
                  />
                </div>
              </ResourceList.Item>
            );
          }}
        />
      </div>
    </Card>
  );
  return (
    <>
      <Page title="Feature Management">
        {featureCard(
          'Features',
          features.filter((f) => !f.isThirdParty) // show non-third party features
        )}
        {featureCard(
          'Third party components',
          features.filter((f) => f.isThirdParty) // show third party features
        )}
      </Page>
      {openModal ? (
        <FeatureConfirmModal
          openModal={openModal}
          feature={selectedFeature}
          onFeatureConfirm={handleFeatureConfirm}
          onCancel={handleCancel}
        />
      ) : null}
    </>
  );
};

export default FeatureList;
